/**
 * useFolderContents Hook
 * 
 * Fetches the contents (folders and documents) of a specific folder path.
 * Used for Google Drive-style single-level view.
 * 
 * @example
 * const { folders, documents, isLoading } =
 *   useFolderContents(caseId, category, currentPath);
 */

import { useQuery } from "@tanstack/react-query";
import { folderHierarchyService, FolderContents } from "@/services/folderHierarchyService";
import { adaptivePollInterval } from "@/lib/adaptivePollInterval";

interface UseFolderContentsOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}

export function useFolderContents(
  caseId: string,
  category: string,
  folderPath: string[],
  subcategory?: string,
  options: UseFolderContentsOptions = {}
) {
  const {
    enabled = true,
    // WHY false par défaut : un refetch au moindre blur/focus de la fenêtre
    // re-déclenche le spinner « Chargement… » de la grille = flicker parasite.
    // Le contenu reste rafraîchi par le polling adaptatif ci-dessous (#238).
    refetchOnWindowFocus = false
  } = options;

  const query = useQuery({
    queryKey: ['folder-contents', caseId, category, subcategory, folderPath],
    queryFn: async () => {
      if (!caseId || !category) {
        return { folders: [], documents: [] } satisfies FolderContents;
      }
      return folderHierarchyService.getFolderContents(caseId, category, folderPath, subcategory);
    },
    enabled: enabled && !!caseId && !!category,
    refetchOnWindowFocus,
    // WHY PAS de `placeholderData: keepPreviousData` (retiré 2026-07-29) : il
    // gardait l'ancienne grille affichée pendant le fetch de la cible pour éviter
    // le trou blanc (#238), au prix d'une fenêtre où l'écran montrait un chemin
    // et l'état de navigation un autre. Cette divergence a produit deux bugs —
    // les paires « Partie adverse » rendues à plat (décision de groupement prise
    // sur la destination, cartes venant de la racine), puis des chemins
    // impossibles au double-clic. On affiche désormais directement la
    // destination, avec `FolderGridSkeleton` le temps de la réponse : un seul
    // chemin de vérité, rien à resynchroniser. Le trou blanc d'origine est réglé
    // par la FORME du squelette, pas en montrant du contenu périmé.
    // Le polling ci-dessous ne fait pas clignoter la grille : à clé inchangée,
    // un refetch d'arrière-plan conserve les données affichées.
    staleTime: 5_000, // 5 seconds initially
    // Polling adaptatif indexé sur le dernier CHANGEMENT de contenu (5s → 10s
    // → 30s) — voir lib/adaptivePollInterval.ts pour le bug que ça corrige
    // (l'ancienne version inline pollait à 5s pour toujours).
    refetchInterval: adaptivePollInterval,
    // Don't poll in background tabs to save resources
    refetchIntervalInBackground: false,
  });

  return {
    folders: query.data?.folders || [],
    documents: query.data?.documents || [],
    isLoading: query.isLoading
  };
}

